// Cloud library client — talks to the /lib endpoint (pv-lib coordinator).
//
// The server holds the canonical EPUBs. PageVoice fetches the
// manifest to browse, and downloads books into local IndexedDB so they
// open fast and work offline. Upload requires a PIN that the user pastes
// once (stored in localStorage).

import { remoteUrl, PUBLIC_MODE } from "./remoteConfig";
import { storeDiag, reportStorageState } from "./storageDiag";

const LIB_BASE = remoteUrl("/lib");

export interface CloudBook {
  id: string;
  title: string;
  author?: string;
  filename: string;
  size: number;
  sha256: string;
  added_at: number;
}

export interface CloudHealth {
  ok: boolean;
  version: string;
  count: number;
  total_bytes: number;
}

let healthPromise: Promise<CloudHealth | null> | null = null;
export function checkCloudLibrary(): Promise<CloudHealth | null> {
  if (PUBLIC_MODE) return Promise.resolve(null);
  if (healthPromise) return healthPromise;
  healthPromise = fetch(`${LIB_BASE}/health`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  return healthPromise;
}

export function recheckCloudLibrary(): Promise<CloudHealth | null> {
  healthPromise = null;
  return checkCloudLibrary();
}

export async function fetchCloudManifest(): Promise<CloudBook[]> {
  if (PUBLIC_MODE) return [];
  const r = await fetch(`${LIB_BASE}/manifest`);
  if (!r.ok) throw new Error(`lib manifest ${r.status}`);
  const data = await r.json();
  const raw: CloudBook[] = data.books ?? [];
  // Filter out smoke-test / placeholder entries. These show up because the
  // upload pipeline is also used by the headless build verifier — they
  // shouldn't be on the user's shelf.
  const books = raw.filter((b) => !isTestBook(b));
  // Persist a copy so the next launch can paint placeholders immediately,
  // before the network round-trip completes. Bounded by the manifest size
  // (a tiny per-book record); we don't store covers or blobs here.
  try {
    localStorage.setItem(MANIFEST_CACHE_KEY, JSON.stringify(books));
  } catch {
    /* localStorage may be blocked — no-op */
  }
  return books;
}

/** Match the synthetic books the smoke harness uploads. Title heuristic
 *  ("PageVoice Test") catches the current pattern; author "Automated Test"
 *  catches future variants. */
function isTestBook(b: CloudBook): boolean {
  return isTestBookLike(b.title, b.author);
}

/** Generic predicate so LibraryView can hide local Dexie books that match
 *  the same smoke-harness pattern. Title-or-author match, case-insensitive. */
export function isTestBookLike(title?: string, author?: string): boolean {
  const t = (title || "").toLowerCase();
  const a = (author || "").toLowerCase();
  return (
    t.includes("pagevoice test") ||
    t.includes("page voice test") ||
    t.includes("smoke test") ||
    a.includes("automated test") ||
    a === "test"
  );
}

const MANIFEST_CACHE_KEY = "pv.cloudManifestCache";

/** Read the last-known cloud manifest from localStorage. Used to paint
 *  placeholder book cards instantly on launch — feels the same on web and
 *  on the home-screen PWA, with no "rebuilding library" pause. */
export function readCachedCloudManifest(): CloudBook[] {
  if (PUBLIC_MODE) return [];
  try {
    const raw = localStorage.getItem(MANIFEST_CACHE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    // Re-apply the test-book filter on read so a previously-cached
    // manifest that included a test entry still hides it after deploy.
    return (arr as CloudBook[]).filter((b) => !isTestBook(b));
  } catch {
    return [];
  }
}

export async function downloadCloudBook(id: string): Promise<Blob> {
  const r = await fetch(`${LIB_BASE}/book/${encodeURIComponent(id)}`);
  if (!r.ok) throw new Error(`lib book ${r.status}`);
  return r.blob();
}

export async function uploadCloudBook(file: File): Promise<CloudBook> {
  const fd = new FormData();
  fd.append("file", file, file.name);
  const r = await fetch(`${LIB_BASE}/upload`, {
    method: "POST",
    body: fd,
  });
  if (r.status === 415) throw new Error("unsupported_format");
  if (r.status === 413) throw new Error("too_large");
  if (!r.ok) throw new Error(`upload ${r.status}`);
  return (await r.json()) as CloudBook;
}

export async function deleteCloudBook(id: string): Promise<void> {
  const r = await fetch(`${LIB_BASE}/book/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!r.ok) throw new Error(`delete ${r.status}`);
}


// ── Cloud-synced reading position ───────────────────────────────────────
//
// Position-by-SHA storage that survives:
//   - iOS PWA reinstall (deleting + re-adding the home-screen icon)
//   - IndexedDB / localStorage eviction
//   - Safari-vs-PWA storage scope split (both see the same value)
//   - moving between devices
//
// Server endpoints: GET / PUT / DELETE /lib/position/<sha> on the server.
// Schema returned by the server:
//   { cfi: string, href?: string, progressPercent?: number, savedAt: number }

export interface CloudPosition {
  cfi: string;
  href?: string;
  progressPercent?: number;
  savedAt: number;
}

/** Fetch the most recently stored position for this EPUB SHA-256, or null
 *  if there isn't one. Silent on network errors — the caller falls back
 *  to local Dexie / localStorage. */
export async function fetchCloudPosition(
  sha: string
): Promise<CloudPosition | null> {
  if (PUBLIC_MODE) return null;
  if (!sha) return null;
  try {
    const r = await fetch(`${LIB_BASE}/position/${encodeURIComponent(sha)}`);
    if (r.status === 404) return null;
    if (!r.ok) return null;
    const data = await r.json();
    if (!data || typeof data.cfi !== "string") return null;
    return data as CloudPosition;
  } catch {
    return null;
  }
}

/** Push a position to the cloud. Best-effort: any failure is swallowed so
 *  the user can keep reading offline without errors surfacing. The server
 *  refuses to overwrite a newer savedAt with an older one, so two devices
 *  racing each other always settle on the most recent write. */
export async function writeCloudPosition(
  sha: string,
  pos: Omit<CloudPosition, "savedAt">,
): Promise<void> {
  if (PUBLIC_MODE) return;
  if (!sha || !pos.cfi) return;
  try {
    await fetch(`${LIB_BASE}/position/${encodeURIComponent(sha)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        cfi: pos.cfi,
        href: pos.href,
        progressPercent: pos.progressPercent,
      }),
    });
  } catch {
    /* silent — cloud is a nice-to-have */
  }
}

/** Bulk-fetch all positions so we can seed a local cache on app boot.
 *  Returns an object keyed by SHA → CloudPosition. */
export async function fetchAllCloudPositions(): Promise<Record<string, CloudPosition>> {
  if (PUBLIC_MODE) return {};
  try {
    const r = await fetch(`${LIB_BASE}/positions`);
    if (!r.ok) return {};
    const data = await r.json();
    return (data && typeof data === "object") ? data : {};
  } catch {
    return {};
  }
}


// ===========================================================================
// Background sync — fold the "Upload book" and "Get" buttons into invisible
// behavior. The UI just shows your library; the cloud is the source of truth.
// ===========================================================================

import { db } from "./db";
import type { Book } from "@/types/book";

/** SHA-256 hex of a Blob. Used to dedupe against what's already on the
 *  cloud so we don't re-upload the same bytes.
 *
 *  Uses SubtleCrypto when available; falls back to a pure-JS SHA-256 on
 *  insecure origins (e.g. the http:// Tailscale hub), where
 *  crypto.subtle is undefined and every book import used to fail
 *  silently (fixed 2026-07-10). */
export async function sha256Hex(blob: Blob): Promise<string> {
  // ── NEVER MATERIALISE THE WHOLE FILE (2026-10-07) ────────────────────
  // This used to be `await blob.arrayBuffer()` — one CONTIGUOUS allocation
  // the size of the book. For Thomas's 55 MB Future Boy that landed on top
  // of the response blob, the File wrapper, the epub parse and the cover
  // extraction, and his BOOX renderer (Chrome 111 WebView, low-RAM e-ink)
  // died before the import could finish.
  //
  // CONFIRMED FROM HIS DEVICE on build 261006-1330, which contains none of
  // today's changes and no reload code at all:
  //   08:20:32  GET /lib/book/40d855621546a86d  200  55,395,633
  //   08:20:38  GET /pagevoice/                 304   <- shell re-requested
  // A completed download followed by the page restarting six seconds
  // later. The renderer was being killed, and the rollback proved it was
  // not anything we shipped.
  //
  // sha256HexStreaming folds the blob through blob.stream() in 1 MB chunks
  // (slice() fallback where streams are missing), so peak extra memory is
  // one chunk. crypto.subtle.digest() cannot help: it has no streaming
  // form and only hashes a buffer already in memory — the exact thing we
  // cannot afford.
  //
  // VERIFIED byte-for-byte against crypto.subtle at 0/1/63/64/65 bytes,
  // 1 MB ± 1, 60 MB, and on the real 55,395,633-byte Future Boy epub,
  // where it reproduces the manifest's own sha256. That mattered: a wrong
  // hash would have silently broken cloud dedupe and position sync.
  const { sha256HexStreaming } = await import("./hashService");
  return sha256HexStreaming(blob);
}

function toHex(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, "0");
  }
  return out;
}

/** Minimal pure-JS SHA-256 (FIPS 180-4). Only used where SubtleCrypto is
 *  unavailable (insecure contexts). */
function sha256Js(data: Uint8Array): Uint8Array {
  const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
    0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
    0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
    0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]);
  const H = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
    0x1f83d9ab, 0x5be0cd19,
  ]);
  const len = data.length;
  const bitLenHi = Math.floor(len / 0x20000000);
  const bitLenLo = (len << 3) >>> 0;
  const padded = new Uint8Array((((len + 8) >> 6) + 1) << 6);
  padded.set(data);
  padded[len] = 0x80;
  const dv = new DataView(padded.buffer);
  dv.setUint32(padded.length - 8, bitLenHi);
  dv.setUint32(padded.length - 4, bitLenLo);
  const w = new Uint32Array(64);
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 =
        ((w[i - 15] >>> 7) | (w[i - 15] << 25)) ^
        ((w[i - 15] >>> 18) | (w[i - 15] << 14)) ^
        (w[i - 15] >>> 3);
      const s1 =
        ((w[i - 2] >>> 17) | (w[i - 2] << 15)) ^
        ((w[i - 2] >>> 19) | (w[i - 2] << 13)) ^
        (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = H;
    for (let i = 0; i < 64; i++) {
      const S1 =
        ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^
        ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 =
        ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^
        ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    H[0] = (H[0] + a) >>> 0; H[1] = (H[1] + b) >>> 0;
    H[2] = (H[2] + c) >>> 0; H[3] = (H[3] + d) >>> 0;
    H[4] = (H[4] + e) >>> 0; H[5] = (H[5] + f) >>> 0;
    H[6] = (H[6] + g) >>> 0; H[7] = (H[7] + h) >>> 0;
  }
  const out = new Uint8Array(32);
  const odv = new DataView(out.buffer);
  for (let i = 0; i < 8; i++) odv.setUint32(i * 4, H[i]);
  return out;
}

/** Pulled from localStorage; default ON. Lets a user kill cloud mirroring
 *  in Settings later without touching the source. */
function cloudSyncEnabled(): boolean {
  if (PUBLIC_MODE) return false;
  try {
    const v = localStorage.getItem("pv.cloudSync");
    return v == null ? true : v === "1";
  } catch {
    return true;
  }
}

/** Silent mirror: upload a freshly-imported book to the cloud if it's not
 *  already there. Errors are swallowed — a book is fully usable locally
 *  even when the server is unreachable. */
export async function autoMirrorBookToCloud(
  file: File,
  _localBook: Book,
): Promise<void> {
  if (!cloudSyncEnabled()) return;
  try {
    const h = await checkCloudLibrary();
    if (!h?.ok) return;
    const sha = await sha256Hex(file);
    const manifest = await fetchCloudManifest().catch(() => [] as CloudBook[]);
    if (manifest.some((b) => b.sha256 === sha)) return;
    await uploadCloudBook(file);
  } catch {
    /* silent — cloud is a "would be nice"; local is the contract */
  }
}

/** Silent inbound sync: pull anything from the cloud that isn't already in
 *  local IndexedDB. Returns the number of books actually downloaded so the
 *  caller can show a one-line confirmation if it wants. */
export async function autoSyncCloudLibrary(opts?: {
  onProgress?: (current: number, total: number, title: string) => void;
  /** Called when a book could not be stored. The shelf shows this rather
   *  than silently rendering a placeholder — the bare catch {} that hid
   *  this is why the problem took a day to see. */
  onError?: (message: string) => void;
}): Promise<number> {
  if (!cloudSyncEnabled()) return 0;
  const h = await checkCloudLibrary();
  if (!h?.ok) return 0;

  let manifest: CloudBook[];
  try {
    manifest = await fetchCloudManifest();
  } catch {
    return 0;
  }
  if (manifest.length === 0) return 0;

  // Build a set of known-local SHA / filename pairs so we don't re-pull.
  const localBooks = await db.books.toArray();
  const known = new Set<string>();
  for (const b of localBooks) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cs = (b as any).cloudSha256 as string | undefined;
    if (cs) known.add(cs);
    if (b.fileName) known.add(b.fileName);
  }

  // Compute the actual to-pull list, excluding anything the user has
  // tombstoned (i.e. explicitly deleted locally).
  const tombs = readTombstones();
  let toPull = manifest.filter(
    (cb) =>
      !known.has(cb.sha256) &&
      !known.has(cb.filename) &&
      !tombs.has(cb.sha256),
  );
  if (toPull.length === 0) return 0;

  // ── NO BULK AUTO-SYNC (2026-10-07) ───────────────────────────────────
  // This used to download EVERY cloud book the device didn't have — 19
  // books, 105.6 MB, over half of it one 55 MB title — on every library
  // mount. On a tethered e-ink tablet that is the wrong default on its own
  // merits, and it is almost certainly what exhausted the origin quota and
  // so lost the blob for the book he actually wanted.
  //
  // Books now arrive ON DEMAND: tapping a cloud entry downloads that one
  // book and opens it (CloudPlaceholderCard already does exactly this).
  //
  // The single exception is the book he is MID-WAY THROUGH. A stored
  // reading position is unambiguous evidence he wants that book on this
  // device, so we pre-fetch the most recently positioned one and nothing
  // else. For Thomas today that is Future Boy, which is the point.
  const { readLastCfiBySha } = await import("./bookStorageService");
  const withPos = toPull
    .map((cb) => ({ cb, at: readLastCfiBySha(cb.sha256)?.savedAt ?? 0 }))
    .filter((x) => x.at > 0)
    .sort((a, b) => b.at - a.at);
  const prioritised = withPos.length > 0 ? [withPos[0].cb] : [];
  if (prioritised.length === 0) {
    storeDiag("noPrefetch", { candidates: String(toPull.length) });
    return 0;
  }
  storeDiag("prefetchOne", {
    title: prioritised[0].title.slice(0, 28),
    sizeMB: String(Math.round((prioritised[0].size ?? 0) / 1048576)),
    skipped: String(toPull.length - 1),
  });
  toPull = prioritised;

  // 3. Ask for persistence BEFORE the large write, not after. Without it
  //    Android evicts large origin data under pressure and a 55 MB epub is
  //    first in that queue.
  await reportStorageState("beforePrefetch");

  // Import lazily so we don't pull bookStorageService at module-load time
  // and end up with a circular import. The dynamic import resolves once at
  // first use and is cached by the bundler from then on.
  const { importEpub } = await import("./bookStorageService");

  let done = 0;
  // ── RE-DOWNLOAD BRAKE (2026-10-07) ───────────────────────────────────
  // Independent of WHY a write fails, never refetch the same large book in
  // a tight loop. MEASURED: the same 55 MB epub pulled three times complete
  // inside eight minutes on a tethered e-ink tablet. Even once retention is
  // fixed, an unstorable book (quota genuinely exhausted, corrupt file)
  // must not cost him his bandwidth and his battery forever.
  //
  // One attempt per book per hour. The record is keyed by sha so it follows
  // the book rather than the manifest id.
  const FAIL_KEY = "pv.syncFailures";
  const COOLDOWN_MS = 60 * 60 * 1000;
  const readFails = (): Record<string, number> => {
    try {
      return JSON.parse(localStorage.getItem(FAIL_KEY) || "{}") as Record<string, number>;
    } catch {
      return {};
    }
  };
  const noteFail = (sha: string) => {
    try {
      const f = readFails();
      f[sha] = Date.now();
      localStorage.setItem(FAIL_KEY, JSON.stringify(f));
    } catch {
      /* ignore */
    }
  };
  const fails = readFails();
  for (const cb of toPull) {
    const lastFail = fails[cb.sha256] ?? 0;
    if (Date.now() - lastFail < COOLDOWN_MS) {
      storeDiag("skippedCooldown", {
        title: cb.title.slice(0, 28),
        minsAgo: String(Math.round((Date.now() - lastFail) / 60000)),
      });
      continue;
    }
    try {
      opts?.onProgress?.(done, toPull.length, cb.title);
      const blob = await downloadCloudBook(cb.id);
      const file = new File([blob], cb.filename || `${cb.title}.epub`, {
        type: "application/epub+zip",
      });
      await importEpub(file);
      // VERIFY the blob actually persisted. A successful-looking write that
      // leaves nothing behind is the failure mode we are chasing: the book
      // stays non-local, so the next sync downloads all 55 MB again.
      try {
        const { getBookBlob } = await import("./bookStorageService");
        const check = await getBookBlob(
          (await (await import("./db")).db.books
            .filter((b) => b.cloudSha256 === cb.sha256)
            .first())?.id ?? "",
        );
        storeDiag("afterImport", {
          title: cb.title.slice(0, 28),
          wantMB: String(Math.round((cb.size ?? 0) / 1048576)),
          gotMB: check ? String(Math.round(check.size / 1048576)) : "0",
          retained: check && check.size > 0 ? "1" : "0",
        });
        if (!check || check.size === 0) {
          noteFail(cb.sha256);
          opts?.onError?.(
            `"${cb.title}" downloaded but wouldn't stay saved — storage may be full.`,
          );
        }
      } catch {
        /* verification is best-effort */
      }
      done++;
    } catch (err) {
      // 2026-10-07 — this used to be a bare `catch {}`. A QuotaExceededError
      // on bookBlobs.put was therefore indistinguishable from success, and
      // the book simply stayed non-local — so every library mount
      // re-downloaded it in full. MEASURED on Thomas's device: the same
      // 55 MB epub fetched three times complete plus one aborted inside
      // eight minutes, ~220 MB, all discarded.
      const e = err as { name?: string; message?: string };
      storeDiag("importFailed", {
        title: cb.title.slice(0, 28),
        sizeMB: String(Math.round((cb.size ?? 0) / 1048576)),
        err: String(e?.name || "unknown").slice(0, 32),
        msg: String(e?.message || "").slice(0, 80),
      });
      void reportStorageState("afterImportFail");
      noteFail(cb.sha256);
      const quota = /quota/i.test(String(e?.name) + String(e?.message));
      opts?.onError?.(
        quota
          ? `Couldn't save "${cb.title}" — device storage is full.`
          : `Couldn't save "${cb.title}" (${e?.name || "error"}).`,
      );
      // Skip any one bad book and keep going; the manifest may have stale
      // entries or the server may have evicted a file between manifest
      // fetch and download.
    }
  }
  return done;
}

/** Settings shim: future Settings panel can toggle this. */
export function setCloudSyncEnabled(on: boolean): void {
  try {
    localStorage.setItem("pv.cloudSync", on ? "1" : "0");
  } catch {
    /* localStorage may be blocked — no-op */
  }
}


// ---------------------------------------------------------------------------
// Tombstones — record SHA-256s the user has explicitly deleted so the
// auto-sync never resurrects them. Stored in localStorage as a small JSON
// array. We trim to the last 200 to keep storage bounded; that's far more
// than any reasonable PageVoice library.
// ---------------------------------------------------------------------------

const TOMBSTONE_KEY = "pv.cloudTombstones";
const TOMBSTONE_LIMIT = 200;

export function readTombstones(): Set<string> {
  try {
    const raw = localStorage.getItem(TOMBSTONE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((x) => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function writeTombstones(s: Set<string>): void {
  try {
    const arr = Array.from(s).slice(-TOMBSTONE_LIMIT);
    localStorage.setItem(TOMBSTONE_KEY, JSON.stringify(arr));
  } catch {
    /* no-op */
  }
}

export function addTombstone(sha: string): void {
  if (!sha) return;
  const s = readTombstones();
  s.add(sha);
  writeTombstones(s);
}


/** Fetch and import a single cloud book on demand — used when the user
 *  taps a placeholder card that hasn't been pulled by auto-sync yet.
 *  Returns the new local Book row's id so the caller can navigate to it. */
export async function pullCloudBookNow(cb: CloudBook): Promise<string> {
  // ── THE PATH HIS TAPS ACTUALLY TAKE (2026-10-07) ─────────────────────
  // Instrumented because the absence of beacons was itself the clue. His
  // device reported `quota` and NOTHING else — no importFailed, no
  // afterImport — because every beacon I had added was inside
  // autoSyncCloudLibrary, and that is not this. Tapping a cloud
  // placeholder comes straight here, and here had no instrumentation at
  // all. An uninstrumented path looks identical to a path that never runs.
  //
  // Beacons are staged so the LAST one received names where it died. If
  // the page is being killed (a 55 MB blob, a File wrapper, a 55 MB
  // arrayBuffer for the SHA and an epub parse, all live at once on a
  // low-RAM Chrome 111 WebView) there will be no error to catch and no
  // final beacon — the absence is the signal.
  const sizeMB = Math.round((cb.size ?? 0) / 1048576);
  storeDiag("pullStart", { title: cb.title.slice(0, 28), sizeMB: String(sizeMB) });
  const blob = await downloadCloudBook(cb.id);
  storeDiag("pullDownloaded", {
    title: cb.title.slice(0, 28),
    gotMB: String(Math.round(blob.size / 1048576)),
  });
  const file = new File([blob], cb.filename || `${cb.title}.epub`, {
    type: "application/epub+zip",
  });
  storeDiag("pullWrapped", { title: cb.title.slice(0, 28) });
  // Dynamic import — same pattern as autoSyncCloudLibrary, avoids a
  // module-load-time circular import with bookStorageService.
  const { importEpub } = await import("./bookStorageService");
  try {
    const book = await importEpub(file);
    storeDiag("pullImported", { title: cb.title.slice(0, 28), id: book.id.slice(0, 8) });
    try {
      const { getBookBlob } = await import("./bookStorageService");
      const back = await getBookBlob(book.id);
      storeDiag("pullVerified", {
        title: cb.title.slice(0, 28),
        retainedMB: back ? String(Math.round(back.size / 1048576)) : "0",
        retained: back && back.size > 0 ? "1" : "0",
      });
    } catch {
      /* verification is best-effort */
    }
    return book.id;
  } catch (err) {
    const e = err as { name?: string; message?: string };
    storeDiag("pullImportThrew", {
      title: cb.title.slice(0, 28),
      err: String(e?.name || "unknown").slice(0, 32),
      msg: String(e?.message || "").slice(0, 80),
    });
    void reportStorageState("afterPullFail");
    throw err;
  }
}

/** Look up a cloud book by SHA-256 and delete it. Silent if not found or
 *  if the cloud isn't reachable. Returns true if a delete actually happened. */
export async function deleteCloudBookBySha(sha: string): Promise<boolean> {
  if (!sha) return false;
  try {
    const h = await checkCloudLibrary();
    if (!h?.ok) return false;
    const manifest = await fetchCloudManifest();
    const cb = manifest.find((b) => b.sha256 === sha);
    if (!cb) return false;
    await deleteCloudBook(cb.id);
    return true;
  } catch {
    return false;
  }
}
